/**
 * Theme state: light / dark / system (CLAUDE.md 14.2).
 *
 * Persistence is two-layer:
 *
 * - `localStorage` is a mirror, read by `public/theme.js` *before first paint* so the
 *   page never flashes the wrong theme;
 * - `users.theme_preference` on the server is the source of truth, so the choice
 *   follows the engineer to another device.
 *
 * This provider owns only the first layer. It is mounted *above* `AuthProvider`,
 * because the login page needs a theme too, and it therefore cannot call the API.
 * `PreferenceSync` (inside the authenticated tree) owns the second layer. Splitting
 * them this way is what keeps the theme working while signed out.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react'

export type ThemeChoice = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

export const THEME_STORAGE_KEY = 'calcdesk.theme'

interface ThemeContextValue {
  /** What the user chose, including "system". */
  choice: ThemeChoice
  /** What that currently resolves to, after consulting the OS. */
  resolved: ResolvedTheme
  setTheme: (choice: ThemeChoice) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

function readStoredChoice(): ThemeChoice {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored
  } catch {
    // Private windows and "block site data" both throw on access rather than
    // returning null. Falling back to "system" is always safe.
  }
  return 'system'
}

function systemTheme(): ResolvedTheme {
  return typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light'
}

export function resolveTheme(choice: ThemeChoice): ResolvedTheme {
  return choice === 'system' ? systemTheme() : choice
}

/** Stamp the root element. "system" removes the attribute so CSS media queries win. */
export function applyTheme(choice: ThemeChoice): void {
  const root = document.documentElement
  if (choice === 'system') {
    root.removeAttribute('data-theme')
  } else {
    root.setAttribute('data-theme', choice)
  }
}

/**
 * Subscribe to the OS colour-scheme preference.
 *
 * `useSyncExternalStore` rather than an effect-plus-setState: the OS preference is
 * precisely an external store, and reading it this way means the resolved theme is
 * computed during render instead of one render *after* the change. The effect version
 * caused a cascading render on every theme switch, and briefly rendered the old theme
 * when the machine flipped at sunset.
 */
function subscribeToSystemTheme(onChange: () => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {}
  const query = window.matchMedia('(prefers-color-scheme: dark)')
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoice] = useState<ThemeChoice>(readStoredChoice)
  const system = useSyncExternalStore<ResolvedTheme>(
    subscribeToSystemTheme,
    systemTheme,
    // Server/prerender snapshot. Light is the safe default: the pre-paint script
    // corrects it before anything is visible.
    () => 'light',
  )

  // Derived, not stored: there is no state here that React has to be told about.
  const resolved: ResolvedTheme = choice === 'system' ? system : choice

  // The one genuinely external thing: stamping the document and mirroring to storage
  // for the pre-paint script. That is what an effect is for.
  useEffect(() => {
    applyTheme(choice)
    try {
      localStorage.setItem(THEME_STORAGE_KEY, choice)
    } catch {
      // Storage being unavailable must not break theming for this session.
    }
  }, [choice])

  const setTheme = useCallback((next: ThemeChoice) => setChoice(next), [])

  const value = useMemo(() => ({ choice, resolved, setTheme }), [choice, resolved, setTheme])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext)
  if (!context) throw new Error('useTheme must be used within a ThemeProvider')
  return context
}
